import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

import { createSignUpFormSchema, type SignUpFormData } from '@/lib/schema/authSchema';
import { signupRequest, type AuthData, type SignupSuccessData } from '@/services/auth/authService';
import { useAuthActions } from '@/store/authStore';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { buildSsoInitiateUrl } from '@/utils/ssoRedirect';

interface SsoDomainInfo {
  organizationId: string;
  organizationName?: string;
  authMethod: 'OIDC' | 'SAML2';
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageTitle(t('auth.signup'));
  const signUpFormSchema = useMemo(() => createSignUpFormSchema(t), [t]);
  const { setLoginDetails } = useAuthActions();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ssoInfo, setSsoInfo] = useState<SsoDomainInfo | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirmPassword: '',
      tos_accept: false,
    },
  });

  // Kontrollera om e-postdomänen tillhör en SSO-organisation. I så fall ska inget
  // lokalt konto skapas – användaren ska logga in via SSO.
  const checkSsoForEmail = async (email: string): Promise<SsoDomainInfo | null> => {
    const trimmed = (email || '').trim();
    if (!trimmed || !trimmed.includes('@')) {
      setSsoInfo(null);
      return null;
    }
    try {
      const res = await api.get('/api/auth/federation/discover', { params: { email: trimmed } });
      const data = res.data?.data;
      if (data?.ssoRequired && data?.organizationId) {
        const method = data.authMethod === 'SAML2' ? 'SAML2' : 'OIDC';
        const info: SsoDomainInfo = {
          organizationId: String(data.organizationId),
          organizationName: data.organizationName,
          authMethod: method,
        };
        setSsoInfo(info);
        return info;
      }
    } catch {
      // Fail-open: tillåt vanlig registrering vid uppslagsfel
    }
    setSsoInfo(null);
    return null;
  };

  const goToSso = (info: SsoDomainInfo, email?: string) => {
    window.location.replace(
      buildSsoInitiateUrl({
        organizationId: info.organizationId,
        authMethod: info.authMethod,
        email,
        redirect: '/',
      })
    );
  };

  const emailField = register('email');

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);

    // SSO-domän: blockera lokal registrering och styr till SSO
    const sso = await checkSsoForEmail(data.email);
    if (sso) {
      toast.error(t('auth.ssoDomainSignupBlocked'));
      goToSso(sso, data.email);
      setIsLoading(false);
      return;
    }

    try {
      const response = await signupRequest({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        tos_accept: data.tos_accept,
      });

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data;

        if (responseData.success) {
          const payload = responseData.data as SignupSuccessData | Record<string, unknown> | undefined;

          if (payload && 'requiresEmailVerification' in payload && payload.requiresEmailVerification) {
            const orgLinked = payload.organizationLinkedByDomain === true;
            toast.success(
              orgLinked ? t('signup.toastSuccessOrgLinked') : t('signup.toastSuccessManual'),
            );
            navigate('/login', {
              state: {
                email: data.email,
                requiresVerification: true,
                organizationLinkedByDomain: orgLinked,
                message: responseData.message,
              },
            });
          } else {
            const authData = responseData.data;
            if (
              authData &&
              typeof authData === 'object' &&
              'accessToken' in authData &&
              'refreshToken' in authData &&
              'user' in authData
            ) {
              const ad = authData as AuthData;
              setLoginDetails({
                accessToken: ad.accessToken,
                refreshToken: ad.refreshToken,
                user: ad.user,
              });
              toast.success(t('login.welcomeMessage'));
              navigate('/');
            } else {
              toast.success(responseData.message || t('signup.submit'));
              navigate('/login');
            }
          }
        }
      }
    } catch (error: unknown) {
      const ax = error as { response?: { data?: { error?: string; message?: string } } };
      const errorMessage =
        ax.response?.data?.error || ax.response?.data?.message || t('signup.createError');

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white dark:bg-white rounded-lg shadow-md p-8">
        <h2 className="text-3xl font-bold text-center mb-2 text-gray-800 dark:text-gray-800">
          {t('signup.title')}
        </h2>
        <p className="text-sm text-center text-textSecondary dark:text-textSecondary mb-6">{t('signup.subtitle')}</p>

        <div className="mb-6 flex gap-3 rounded-lg border border-blue-100 bg-blue-50/90 dark:bg-blue-950/20 dark:border-blue-900 p-3 text-sm text-gray-800 dark:text-gray-200">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{t('signup.infoBoxTitle')}</p>
            <p className="mt-1 text-gray-700 dark:text-gray-300">{t('signup.infoBoxBody')}</p>
            <p className="mt-2 text-gray-600">{t('signup.infoBoxManual')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="first_name" className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2">
                {t('signup.firstNameLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                id="first_name"
                type="text"
                autoComplete="given-name"
                {...register('first_name')}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.first_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
                }`}
                placeholder={t('signup.firstNamePlaceholder')}
                disabled={isLoading}
              />
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-500">{errors.first_name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2">
                {t('signup.lastNameLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                id="last_name"
                type="text"
                autoComplete="family-name"
                {...register('last_name')}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.last_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
                }`}
                placeholder={t('signup.lastNamePlaceholder')}
                disabled={isLoading}
              />
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-500">{errors.last_name.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2">
              {t('signup.emailLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              {...emailField}
              onBlur={(e) => {
                emailField.onBlur(e);
                void checkSsoForEmail(e.target.value);
              }}
              className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
              }`}
              placeholder={t('signup.emailPlaceholder')}
              disabled={isLoading}
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
            {ssoInfo && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3 text-sm">
                <p className="text-amber-800 dark:text-amber-200">{t('auth.ssoDomainSignupBlocked')}</p>
                <button
                  type="button"
                  onClick={() => goToSso(ssoInfo, getValues('email'))}
                  className="mt-2 inline-flex items-center font-semibold text-blue-600 hover:text-blue-800"
                >
                  {t('auth.useSsoInstead')}
                </button>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2">
              {t('signup.passwordLabel')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('password')}
                className={`w-full px-3 py-2 pr-10 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
                }`}
                placeholder={t('signup.passwordPlaceholder')}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
                disabled={isLoading}
                aria-label={showPassword ? t('copy.admin.hidePassword') : t('copy.admin.showPassword')}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-600">{t('signup.passwordRulesHint')}</p>
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-gray-700 dark:text-gray-700 text-sm font-bold mb-2"
            >
              {t('signup.confirmPasswordLabel')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                {...register('confirmPassword')}
                className={`w-full px-3 py-2 pr-10 border rounded-md bg-white dark:bg-white text-gray-900 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-300'
                }`}
                placeholder={t('signup.confirmPasswordPlaceholder')}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
                disabled={isLoading}
                aria-label={showConfirmPassword ? t('copy.admin.hidePassword') : t('copy.admin.showPassword')}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex items-start">
            <input
              id="tos_accept"
              type="checkbox"
              {...register('tos_accept')}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="tos_accept" className="ml-2 text-sm text-gray-700 dark:text-gray-700">
              {t('signup.tosPrefix')}{' '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-800">
                {t('signup.tosTerms')}
              </Link>{' '}
              {t('signup.tosAnd')}{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-800">
                {t('signup.tosPrivacy')}
              </Link>{' '}
              <span className="text-red-500">*</span>
            </label>
          </div>
          {errors.tos_accept && <p className="text-sm text-red-500">{errors.tos_accept.message}</p>}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full bg-blue-500 text-white font-bold py-2 px-4 rounded-md hover:bg-blue-600 transition duration-200 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? t('signup.creating') : t('signup.submit')}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-textSecondary dark:text-textSecondary">
          {t('signup.hasAccount')}{' '}
          <Link to="/login" className="text-blue-600 hover:text-blue-800 font-semibold">
            {t('signup.loginLink')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
